import { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';

import { ErrorPage } from '~/components/ErrorPage';
import { useUser } from '~/modules/auth/hooks/useUser.ts';
import { useRemoteContexts } from '~/modules/remotes/hooks/use-remote-contexts.ts';
import { useRemotes } from '~/modules/remotes/hooks/useRemotes.ts';
import type { IRemoteDeclaration } from '~/modules/remotes/types.ts';

import { ErrorBoundary } from '../errors/components/ErrorBoundary';
import { RemotesStatus } from './components/RemotesStatus';

interface PrivateRemoteProps {
  remote: IRemoteDeclaration;
}

export function PrivateRemote({ remote }: PrivateRemoteProps) {
  const remoteContexts = useRemoteContexts();
  const { hasPermission, ...user } = useUser();

  if (remote?.permission(hasPermission, { user })) {
    return (
      <Suspense>
        <div
          className={[
            'w-full',
            remoteContexts[remote.name].isolateStyles ? 'no-preflight' : '',
          ].join(' ')}
        >
          <remote.root ctx={remoteContexts[remote.name].context} />
        </div>
      </Suspense>
    );
  }

  return <ErrorPage type="noAccessPage" />;
}

PrivateRemote.displayName = 'PrivateRemote';

/**
 * Uniform Router.
 */
export function RemoteRouter() {
  const { remotes } = useRemotes();

  return (
    <ErrorBoundary>
      <Routes>
        {remotes.map((remote) => (
          <Route
            key={remote.name}
            path={remote.path[0]}
            errorElement={<ErrorPage />}
            element={<PrivateRemote remote={remote} />}
          />
        ))}

        <Route path="_dev/remotes-status" element={<RemotesStatus />} />
      </Routes>
    </ErrorBoundary>
  );
}

RemoteRouter.displayName = 'RemoteRouter';
